import React, { useState, useRef, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Send, MessageCircle, ChevronDown, Loader2, HelpCircle } from 'lucide-react';
import { responses } from './response';
import { getEnhancedResponse, findBestMatch, calculateSimilarity, extractKeywords } from './utils';

const TYPING_SPEED = 30;
const MAX_RETRIES = 3;

const WELCOME_MESSAGES = [
  {
    id: 'welcome-1',
    type: 'bot',
    text: "👋 Welcome! I'm your marketing assistant."
  },
  
];

const ChatBot = ({ isFirstLoad = true }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [showFAQ, setShowFAQ] = useState(false);
  const [suggestedQuestions, setSuggestedQuestions] = useState([]);
  const welcomeShownRef = useRef(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const showWelcomeMessages = async () => {
      if (isFirstLoad && !welcomeShownRef.current) {
        welcomeShownRef.current = true;
        for (const msg of WELCOME_MESSAGES) {
          setIsTyping(true);
          await typeMessage(msg.text);
          setMessages(prev => [...prev, msg]);
          setIsTyping(false);
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }
    };
    showWelcomeMessages();
  }, [isFirstLoad]);

  const typeMessage = async (text) => {
    const words = text.split(' ');
    let currentText = '';
    
    for (let word of words) {
      currentText += word + ' ';
      await new Promise(resolve => setTimeout(resolve, TYPING_SPEED));
    }
    
    return currentText.trim();
  };

  const handleFAQClick = async (question) => {
    const faqItem = responses.faq.find(f => f.question === question);
    if (faqItem) {
      setShowFAQ(false);
      setMessages(prev => [...prev, 
        { id: Date.now(), type: 'user', text: faqItem.question },
        { id: Date.now() + 1, type: 'bot', text: faqItem.answer }
      ]);
    }
  };

  const findSimilarQuestions = (input) => {
    const threshold = 0.3;
    return responses.faq
      .map(faq => ({
        question: faq.question,
        similarity: calculateSimilarity(input, faq.question)
      }))
      .filter(item => item.similarity > threshold)
      .sort((a, b) => b.similarity - a.similarity)
      .slice(0, 3)
      .map(item => item.question);
  };

  const updateSuggestedQuestions = (input) => {
    if (input.trim().length > 2) {
      const similar = findSimilarQuestions(input);
      setSuggestedQuestions(similar);
    } else {
      setSuggestedQuestions([]);
    }
  };

  const getResponse = (input) => {
    return getEnhancedResponse(input, responses);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!input.trim() || isTyping) return;

    const userMessage = { id: Date.now(), type: 'user', text: input };
    setMessages(prev => [...prev, userMessage]);
    setSuggestedQuestions([]);
    setInput('');

    const exitPhrases = ["thank you", "thanks", "bye", "have a nice day", "goodbye"];
    const normalizedInput = input.toLowerCase();
  
    if (exitPhrases.some((phrase) => normalizedInput.includes(phrase))) {
      const exitResponse = "You're welcome! Have a great day! 😊";
      setIsTyping(true);
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate typing delay
      setMessages((prev) => [
        ...prev,
        { id: Date.now(), type: 'bot', text: exitResponse },
      ]);
      setIsTyping(false);
      setTimeout(() => {
        setInput(''); // Clear input for clean exit
      }, 1500);
      return;
    }

    try {
      setIsTyping(true);
      const response = getResponse(input);
      const typedResponse = await typeMessage(response);
      setMessages(prev => [...prev, { id: Date.now(), type: 'bot', text: typedResponse }]);

      // Find related questions for follow-up
      const relatedQuestions = findSimilarQuestions(input);
      if (relatedQuestions.length > 0) {
        await new Promise(resolve => setTimeout(resolve, 500));
        setMessages(prev => [...prev, {
          id: Date.now() + 2,
          type: 'bot',
          text: "You might also be interested in:",
          suggestions: relatedQuestions
        }]);
      }

      setRetryCount(0);
    } catch (error) {
      if (retryCount < MAX_RETRIES) {
        setRetryCount(prev => prev + 1);
        handleSubmit();
      } else {
        setMessages(prev => [...prev, {
          id: Date.now(),
          type: 'bot',
          text: "I'm having trouble right now. Please try again later."
        }]);
      }
    } finally {
      setIsTyping(false);
    }
  };

  return (
    <div className="w-full max-w-md rounded-2xl shadow-[0_0_50px_rgba(139,92,246,0.3)] bg-gradient-to-br from-purple-900/40 via-indigo-900/40 to-violet-900/40 backdrop-blur-md overflow-hidden border border-white/10">
    <motion.div 
      className="p-4 flex items-center justify-between bg-gradient-to-r from-purple-500/30 to-violet-500/30 backdrop-blur-sm border-b border-white/10"
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-full bg-white/10 backdrop-blur-sm">
          <MessageCircle className="w-5 h-5 text-purple-200" />
        </div>
        <h2 className="text-lg font-bold text-white/90">Marketing Assistant</h2>
      </div>
        <div className="flex gap-2 relative">
  <motion.button
    whileHover={{ scale: 1.2, rotate: 10 }}
    whileTap={{ scale: 0.9 }}
    animate={{
      y: [0, -5, 0], // Subtle bounce effect
    }}
    transition={{
      duration: 1.5,
      repeat: Infinity,
      repeatType: "loop",
    }}
    onClick={() => setShowFAQ(!showFAQ)}
    className="p-3 rounded-full bg-gradient-to-r from-purple-500 to-violet-500 text-white shadow-lg shadow-purple-500/20 hover:shadow-purple-500/40 transition-all"
  >
    <HelpCircle className="w-6 h-6" />
  </motion.button>
  {showFAQ ? null : (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
      className="absolute -top-10 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-purple-700 to-violet-700 text-white text-xs rounded-md py-1 px-2 shadow-lg"
    >
      
    </motion.div>
  )}
        </div>

      </motion.div>

      <div className="relative">
        <AnimatePresence>
          {showFAQ && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-br from-purple-900/95 via-indigo-900/95 to-violet-900/95 backdrop-blur-md p-4 max-h-[400px] overflow-y-auto scrollbar-thin scrollbar-thumb-purple-500/20"
            >
              <h3 className="text-white font-semibold mb-4">Frequently Asked Questions</h3>
              <div className="space-y-2">
                {responses.faq.map((faq) => (
                  <motion.button
                    key={faq.question}
                    whileHover={{ scale: 1.02 }}
                    onClick={() => handleFAQClick(faq.question)}
                    className="w-full p-3 text-left rounded-lg bg-white/10 hover:bg-white/20 text-white/90 transition-colors"
                  >
                    {faq.question}
                  </motion.button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="h-[400px] overflow-y-auto p-4 space-y-4 scrollbar-thin scrollbar-thumb-purple-500/20 scrollbar-track-transparent">
          <AnimatePresence>
            {messages.map(msg => (
              <motion.div
                key={msg.id}
                className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
              >
                <div className={`max-w-[80%] p-3 rounded-2xl backdrop-blur-sm ${
                  msg.type === 'user' 
                    ? 'bg-gradient-to-r from-purple-500/80 to-violet-500/80 text-white shadow-lg shadow-purple-500/20'
                    : 'bg-white/10 text-white/90 border border-white/10'
                }`}>
                  {msg.text}
                  {msg.suggestions && (
                    <div className="mt-2 space-y-2">
                      {msg.suggestions.map((suggestion, index) => (
                        <button
                          key={index}
                          onClick={() => handleFAQClick(suggestion)}
                          className="block w-full text-left text-sm text-purple-200 hover:text-purple-100 transition-colors"
                        >
                          • {suggestion}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
            {isTyping && (
              <motion.div
                className="flex items-center gap-2 text-purple-200/70 p-2"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">Typing...</span>
              </motion.div>
            )}
          </AnimatePresence>
          <div ref={messagesEndRef} />
        </div>

        <form onSubmit={handleSubmit} className="p-4 bg-black/10 backdrop-blur-md border-t border-white/10">
          <div className="relative">
            <div className="flex gap-2">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => {
                  setInput(e.target.value);
                  updateSuggestedQuestions(e.target.value);
                }}
                placeholder="Type your message..."
                className="flex-1 px-4 py-2 rounded-xl bg-white/5 text-white placeholder-white/40 backdrop-blur-sm border border-white/10 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-transparent transition-all"
              />
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                disabled={!input.trim() || isTyping}
                className="p-2 rounded-xl bg-gradient-to-r from-purple-500 to-violet-500 text-white disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-purple-500/20 hover:shadow-purple-500/40 transition-all"
              >
                <Send className="w-5 h-5" />
              </motion.button>
            </div>
            {suggestedQuestions.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                className="absolute bottom-full left-0 right-0 mb-2 p-2 bg-gradient-to-br from-purple-900/95 via-indigo-900/95 to-violet-900/95 backdrop-blur-md rounded-lg border border-white/10"
              >
                {suggestedQuestions.map((question, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      setInput(question);
                      setSuggestedQuestions([]);
                    }}
                    className="block w-full text-left p-2 text-sm text-white/90 hover:bg-white/10 rounded-lg transition-colors"
                  >
                    {question}
                  </button>
                ))}
              </motion.div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChatBot;